import {useAppDispatch, useNavigate} from 'hooks';
import {RolesEnum} from 'permissions/enums';
import {permissionsManager} from 'permissions/services';
import {hasRequiredPermissions, mapB2CRoles} from 'permissions/utils';
import {useCallback, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {fetchUserRoleThunk, permissionsStateSelector, userRoleListStateSelector} from '../store';
import {userProfileSelector} from 'user';

export function usePermissions() {
  const dispatch = useAppDispatch();
  const userProfile = useSelector(userProfileSelector);
  const {loading: permissionsLoading, requestTriggered: permissionsRequestTriggered} = useSelector(
    permissionsStateSelector
  );
  const {push} = useNavigate();

  // Map roles from OIDC token
  useEffect(() => {
    if (userProfile) {
      // Extract roles from the token. B2C raw tokens carry a comma-separated
      // extension_Roles string; tokens minted by the JASA IdentityServer carry
      // the standard OIDC 'role' claim (a string for one role, an array for many).
      const tokenRoles =
        userProfile.extension_Roles ||
        userProfile.role ||
        userProfile.roles ||
        userProfile['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
        [];

      // Update permissionsManager with token roles
      permissionsManager.setActiveUserRoles(mapB2CRoles(tokenRoles));
    }
  }, [userProfile]);

  const userRoles = permissionsManager.getActiveUserRoles();

  const fetchUserRole = useCallback(() => {
    dispatch(fetchUserRoleThunk({push}));
  }, [dispatch, push]);

  const hasPermission = useCallback(
    (requiredRoles: RolesEnum[]) => {
      return hasRequiredPermissions(userRoles, requiredRoles);
    },
    [userRoles]
  );

  const allUserRoles = useSelector(userRoleListStateSelector);

  return {fetchUserRole, permissionsLoading, permissionsRequestTriggered, userRoles, allUserRoles, hasPermission};
}
